// src/pages/TermsAndConditions.tsx
import React, { useEffect } from "react";

export default function TermsAndConditions() {
  useEffect(() => {
  document.title = "Terms and Conditions • BacklinkBanana";
}, []);

return (
  <div className="max-w-4xl mx-auto px-4 py-10">
    {/* Title & Definitions */}
    <h1 className="text-3xl font-bold mb-6">Terms &amp; Conditions</h1>
    <p className="mb-2">
      <strong>Effective Date:</strong> 31/05/2025
    </p>

    <p className="mb-4">
      Welcome to <strong>BacklinkBanana</strong> (the “Service”). These Terms &amp; Conditions (“T&amp;C”) govern
      your use of https://backlinkbanana.com. By registering for or using the Service in any way, you (“User” or “you”)
      agree to be bound by these T&amp;C. If you do not agree, please do not access or use the Service.
    </p>

    <p className="mb-4">
      <strong>Definitions:</strong><br />
      – “Service” means the website, tools, APIs, and features provided by BacklinkBanana.<br />
      – “Account” means your unique user registration, authenticated via Google, through which you access the Service.<br />
      – “Credits” are the units that permit you to perform searches or data fetches within the Service.<br />
      – “BacklinkBanana,” “we,” “us,” or “our” refers to BacklinkBanana.com, headquartered in India.
    </p>

    {/* Overview */}
    <h2 className="text-xl font-semibold mt-6 mb-2">1. Overview</h2>
    <p className="mb-4">
      BacklinkBanana is a web‐based software (the “Service”) designed to help SEO professionals and website owners
      locate highly relevant websites for backlink acquisitions. Our platform issues search queries to Google Custom Search API and
      Moz API to retrieve metrics (Domain Authority, Page Authority, Spam Score) for each result.
    </p>
    <p className="mb-4">
      The Service is provided on an “as‐is” and “as‐available” basis. We make no warranties (express or implied)
      regarding the reliability, accuracy, or completeness of third‐party data. Use of the Service is at your own risk.
    </p>

    {/* Account Registration */}
    <h2 className="text-xl font-semibold mt-6 mb-2">2. Account Registration</h2>
    <ul className="list-disc ml-6 mb-4">
      <li>
        You must be at least 18 years old to register. By creating an Account, you represent that you meet this age
        requirement.
      </li>
      <li>
        Registration is via Google Sign-In; you must use a valid Google‐authenticated email address. By signing in
        with Google, you grant us access to your email address and basic profile (name, profile picture). We do not
        request any other Google permissions.
      </li>
      <li>
        You are solely responsible for maintaining the confidentiality of your login credentials (Google token, JWT
        cookie, etc.). You agree not to share your Account or authentication tokens with any third party. Any activity
        under your Account is your responsibility.
      </li>
      <li>
        We reserve the right to suspend or terminate your Account if we suspect misuse, fraud, or breach of these
        Terms.
      </li>
    </ul>

    {/* Credit System */}
    <h2 className="text-xl font-semibold mt-6 mb-2">3. Credit System</h2>
    <p className="mb-4">
      Access to certain features of the Service (e.g., backlink searches, domain‐metrics lookups) is regulated by a
      credit‐based system. Credits can be purchased via Razorpay. Refer to our homepage and profile page for current pricing.
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>
        <strong>Credit Deduction:</strong> Each complete search query (up to 20 results) deducts 20 Credits. Fetching
        additional pages or metrics also deducts Credits according to the published rate on the Purchase Page.
      </li>
      <li>
        <strong>Credit Expiry:</strong> Purchased Credits are valid for lifetime (as far as our app runs).
      </li>
      <li>
        <strong>Refunds:</strong> We operate a strict No‐Refund policy (see Section 4 below). All Credit purchases
        are final.
      </li>
      <li>
        <strong>Limits:</strong> We may impose daily, hourly, or per‐query limits (for example, a maximum of 200
        searches per 24‐hour period). These limits are subject to change, and we will notify you by email if limits
        are adjusted.
      </li>
      <li>
        <strong>Duplicate or Cached Searches:</strong> If you repeat the exact same search query (including identical
        filters/exclusions) within a 24‐hour window, you will not be charged an extra Credit; we will serve the
        cached result instead.
      </li>
      <li>
        <strong>Price Changes:</strong> We reserve the right to modify Credit pricing or Credit‐usage rules at any
        time. Existing Credits will remain valid; new pricing applies only to future purchases.
      </li>
    </ul>

    {/* No Refund Policy */}
    <h2 className="text-xl font-semibold mt-6 mb-2">4. No Refund Policy</h2>
    <p className="mb-4">
      <strong>All purchases of Credits are final. We do not offer any refunds.</strong>
      This includes, but is not limited to:
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>Unavailability, downtime, or technical issues arising from third‐party APIs.</li>
      <li>Incomplete or partial results returned by Google Custom Search or Moz APIs.</li>
      <li>Unused Credits at expiration.</li>
      <li>Partial usage of purchased Credits.</li>
    </ul>
    <p className="mb-4">
      Payments are processed by Razorpay. Any inquiries regarding payment disputes or fraudulent charges must be
      directed to Razorpay, and you may also contact us at{" "}
      <a href="mailto:support@backlinkbanana.com" className="text-blue-600 hover:underline">
        support@backlinkbanana.com
      </a>{" "}
      to assist in investigation. To the extent permitted by law, no refunds will be issued by BacklinkBanana Tech
      Pvt. Ltd.
    </p>

    {/* API Limitations & Disclaimer */}
    <h2 className="text-xl font-semibold mt-6 mb-2">5. API Limitations &amp; Disclaimer</h2>
    <p className="mb-4">
      BacklinkBanana leverages the following third‐party APIs:
      <ul className="list-disc ml-6 mb-2">
        <li><strong>Google Custom Search API</strong></li>
        <li><strong>Moz API</strong></li>
      </ul>
      We do not control these APIs, and we make no representations or warranties (express or implied) regarding the
      accuracy, reliability, or timeliness of any data returned. Because the output from these APIs is beyond our control,
      we disclaim all responsibility for the results. Use of the Service is at your own risk.
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>
        We disclaim any liability for inaccurate, incomplete, or outdated results provided by Google or Moz.
      </li>
      <li>
        We are not responsible for any downtime, rate limits, incomplete results, or quota restrictions imposed by these APIs. If an API is unavailable or returns partial data, certain features of the Service may not function as expected. In such cases, any Credits deducted for a partial response will still be marked as used, since our platform is also charged by the third‐party provider for that request.
      </li>
      <li>
        Any websites or domain suggestions provided by the Service are not endorsements by BacklinkBanana. You should
        independently verify a site’s legitimacy, SEO metrics, and compliance before outreach.
      </li>
    </ul>
    <p className="mb-4">
      By using the Service, you acknowledge and accept these limitations and disclaimers. Use of any data obtained
      via the Service is at your own risk.
    </p>

    {/* Acceptable Use */}
    <h2 className="text-xl font-semibold mt-6 mb-2">6. Acceptable Use</h2>
    <ul className="list-disc ml-6 mb-4">
      <li>
        <strong>Account Security:</strong> You are responsible for safeguarding your access credentials (Google OAuth
        token, JWT cookie). Do not share them with anyone. We reserve the right to suspend any Account if we suspect
        unauthorized access.
      </li>
      <li>
        <strong>No Unauthorized Automation:</strong> Automated scripts, bots, scrapers, or any form of non-human
        interaction are prohibited. Any attempt to overload, scrape, abuse, or reverse‐engineer the API or UI may
        result in immediate termination of your Account.
      </li>
      <li>
        <strong>No Spam or Illicit Use:</strong> You may not use BacklinkBanana to facilitate spam (unsolicited bulk
        outreach) or to propagate malware. You must comply with all applicable laws (including CAN‐SPAM, GDPR, etc.).
      </li>
      <li>
        <strong>Fair Usage:</strong> We reserve the right to set and enforce usage limits (e.g., maximum number of
        searches per day). If your usage materially disrupts the Service or other Users, we may throttle or suspend
        your access.
      </li>
      <li>
        <strong>Monitoring &amp; Audits:</strong> We may monitor logs and usage patterns to detect abuse. Without
        notice, we can audit your Account and suspend or terminate it if we determine that you have violated these
        Acceptable Use terms.
      </li>
    </ul>

    {/* Termination */}
    <h2 className="text-xl font-semibold mt-6 mb-2">7. Termination</h2>
    <p className="mb-4">
      We reserve the right, in our sole discretion, to suspend or terminate your Account or access to the Service at
      any time, with or without notice, if we determine that you have:
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>Violated any provision of these Terms &amp; Conditions.</li>
      <li>Engaged in fraudulent or abusive behavior.</li>
      <li>Infringed on the intellectual property rights of third parties.</li>
      <li>Attempted to compromise system security or disrupt other Users.</li>
    </ul>
    <p className="mb-4">
      Upon termination, your Account will be deactivated, and any unused Credits will be forfeited. We may delete or
      anonymize your data (search history, usage logs) after a grace period of 30 days. Sections concerning 
      Limitation of Liability, Governing Law, Dispute Resolution, and any other provisions that by their nature 
      “survive” termination shall remain in effect.
    </p>

    {/* Limitation of Liability */}
    <h2 className="text-xl font-semibold mt-6 mb-2">8. Limitation of Liability</h2>
    <p className="mb-4">
      To the fullest extent permitted by applicable law, BacklinkBanana.com, its affiliates, officers,
      directors, employees, and agents (collectively “BacklinkBanana”) shall not be liable for any:
    </p>
    <ul className="list-disc ml-6 mb-4">
      <li>
        Indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenue, or
        data, whether in an action in contract, tort (including negligence), strict liability, or otherwise.
      </li>
      <li>
        Claim by any third party for damages arising out of your use or inability to use the Service.
      </li>
      <li>
        Inaccuracies, omissions, or outdated information provided by third‐party APIs (Google, Moz, etc.).
      </li>
    </ul>
    <p className="mb-4">
      Because we do not control the results of Google or Moz APIs, and the output is entirely determined by those third parties,
      we disclaim all responsibility for the results you receive. Use of the Service is at your own risk.
    </p>
    <p className="mb-4">
      Some jurisdictions do not allow the exclusion or limitation of certain damages; if you reside in such a
      jurisdiction, the above exclusions may not apply to you. In that case, BacklinkBanana’s liability is limited
      to the greatest extent permitted by law.
    </p>

    {/* Governing Law & Dispute Resolution */}
    <h2 className="text-xl font-semibold mt-6 mb-2">9. Governing Law &amp; Dispute Resolution</h2>
    <p className="mb-4">
      These T&amp;C shall be governed by and construed in accordance with the laws of India, without regard to its
      conflict of law provisions. You agree that any dispute, claim, or controversy arising out of or in connection
      with these T&amp;C or your use of the Service will be exclusively submitted to the competent courts located in
      Mumbai, Maharashtra, India.
    </p>
    <p className="mb-4">
      <strong>Severability:</strong> If any provision of these T&amp;C is held to be invalid or unenforceable by a 
      court of competent jurisdiction, the remaining provisions will continue in full force and effect.
    </p>

    {/* Changes to Terms */}
    <h2 className="text-xl font-semibold mt-6 mb-2">10. Changes to These Terms</h2>
    <p className="mb-4">
      We reserve the right to modify or amend these Terms &amp; Conditions at any time. When we do, we will update 
      the “Last Updated” date above. If changes are material, we may also send you an email notification (where 
      possible) or post a prominent announcement on the homepage. Continued use of the Service after any changes 
      indicates your acceptance of the updated T&amp;C.
    </p>

    {/* Entire Agreement */}
    <h2 className="text-xl font-semibold mt-6 mb-2">11. Entire Agreement</h2>
    <p className="mb-4">
      These Terms &amp; Conditions, together with our Privacy Policy and any other legal notices published by
      BacklinkBanana on the Service, constitute the entire agreement between you and BacklinkBanana regarding the
      Service and supersede any prior or contemporaneous agreements, representations, or understandings.
    </p>

    {/* Contact Information */}
    <h2 className="text-xl font-semibold mt-6 mb-2">12. Contact Information</h2>
    <p className="mb-4">
      If you have any questions or concerns about these Terms &amp; Conditions, you may contact us at:
    </p>
    <p className="mb-4">
      <strong>Email:</strong> <a href="mailto:support@backlinkbanana.com" className="text-blue-600 hover:underline">support@backlinkbanana.com</a><br/>
      <strong>Postal Address:</strong><br/>
      BacklinkBanana<br/>
      Sector 11, Chandigarh<br/>
      India
    </p>
    <p className="mb-4">
      We aim to respond to all inquiries within 48 business hours.
    </p>
  </div>
);

}
